import React, { useState, useEffect, useRef } from "react";

export default function MonitorDisplay() {
  const [text1, setText1] = useState("");
  const [text2, setText2] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const fullText = "Hello world?";
  const sectionRef = useRef<HTMLDivElement>(null);

  // Intersection Observer to detect when component is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isInView]);

  useEffect(() => {
    // Only start typing if component is in view
    if (!isInView) return;
    
    // First line typing effect
    if (text1.length < fullText.length) {
      const timeout = setTimeout(() => {
        setText1(fullText.slice(0, text1.length + 1));
      }, 150);
      return () => clearTimeout(timeout);
    } else if (!text2) {
      // Start second line after first line is complete
      setText2(">");
    }
  }, [text1, text2, isInView]);

  // Blinking cursor effect
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div ref={sectionRef} className="relative w-full flex flex-col items-center justify-center mt-20 mb-32">
      {/* Monitor Frame */}
      <div className="relative">
        {/* Monitor Border - Outer */}
        <div className="w-[320px] h-[180px] bg-black border border-gray-800 rounded-lg overflow-hidden p-[1px]">
          {/* Monitor Border - Inner */}
          <div className="w-full h-full border border-gray-800 rounded-lg overflow-hidden">
            {/* Monitor Screen with realistic effects */}
            <div 
              className="w-full h-full bg-[#000304] flex flex-col items-center justify-center p-4 relative"
              style={{
                background: 'linear-gradient(135deg, #000304 0%, #001214 100%)',
                boxShadow: 'inset 0 0 30px rgba(0, 0, 0, 0.8)'
              }}
            >
              {/* Screen overlay effects */}
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0px, rgba(0, 0, 0, 0.1) 1px, transparent 1px, transparent 2px)',
                  opacity: '0.3'
                }}
              />
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle at center, transparent 50%, rgba(0, 0, 0, 0.3) 100%)'
                }}
              />
              
              {/* Text content with glow */}
              <div className="relative z-10 text-center">
                <div className="font-mono text-[#2bbc2b] text-base drop-shadow-[0_0_2px_rgba(43,188,43,0.3)]">
                  &gt;{text1}
                </div>
                <div className="font-mono text-[#2bbc2b] text-base drop-shadow-[0_0_2px_rgba(43,188,43,0.3)]">
                  {text2}
                  <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}>
                    _
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Monitor Stand */}
        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
          {/* Stand Neck - Double Line */}
          <div className="relative">
            <div className="w-16 h-8 bg-[#1a1a1a] rounded-b-lg p-[1px]">
              <div className="w-full h-full border border-gray-800 rounded-b-lg"></div>
            </div>
          </div>
          {/* Stand Base - Double Line */}
          <div className="relative">
            <div className="w-24 h-2 bg-[#1a1a1a] mt-1 rounded-lg mx-auto p-[0.5px]">
              <div className="w-full h-full border border-gray-800 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Arrow pointing down */}
      <div className="mt-12 text-2xl text-white/60 animate-bounce">
        v
      </div>
    </div>
  );
} 